import {
  processTableForAnalysis,
  storeExtractionResults,
  applyConfidenceFilterToTableData,
} from "./csvUtils";
import { useCallback, useEffect } from "react";

/**
 * Custom hook for handling table selection changes
 * @param {object} params - Parameters object containing all dependencies
 * @returns {function} handleTableChange function
 */
export const useTableChangeHandler = ({
  tables,
  selectedTable,
  tableData,
  setSelectedTable,
  setSelectedCell,
  setEditingCell,
  setEditValue,
  clearHighlights,
  setDeletedColumns,
  setDeletedRows,
  setCurrentPage,
  setTableData,
  initializeTableIdsInternal,
  setColumnIds,
  setRowIds,
  setOriginalTableStructure,
  cachedTableData,
  setCachedTableData,
  // Grid line state setters
  setDynamicGridLines,
  setOriginalGridLines,
  setPendingGridLineChanges,
  setIsDraggingGridLine,
  setDraggingGridLine,
  setIsGridLineInPreview,
  setPendingNewGridLines,
  setIsPlacingGridLine,
}) => {
  const handleTableChange = useCallback(
    (event) => {
      const tableId = event.target.value;

      // Save current table state before switching
      if (selectedTable && tableData) {
        const currentTableId = selectedTable.table_id || selectedTable.id;
        setCachedTableData((prev) => ({
          ...prev,
          [currentTableId]: tableData,
        }));
      }

      // Find table by various ID formats (exclude separators)
      const table = tables.find(
        (t) =>
          !t.isSeparator &&
          (t.table_id === tableId ||
            t.id === tableId ||
            `table_${tables.indexOf(t)}` === tableId)
      );

      setSelectedTable(table);
      setSelectedCell(null);
      setEditingCell(null);
      setEditValue("");
      clearHighlights();

      // Clear deletion indicators when switching tables - use new Map format
      setDeletedColumns(new Map());
      setDeletedRows(new Map());

      // Clear grid line states when switching tables
      // This ensures grid lines from one table don't render on another table
      setDynamicGridLines({});
      setOriginalGridLines(null);
      setPendingGridLineChanges(null);
      setIsDraggingGridLine(false);
      setDraggingGridLine(null);
      setIsGridLineInPreview(false);
      setPendingNewGridLines([]);
      setIsPlacingGridLine(false);

      // Initialize editable table data
      if (table) {
        // Navigate to the table's page
        const cellKeys = Object.keys(table).filter((key) =>
          key.match(/^\d+_\d+$/)
        );
        if (cellKeys.length > 0) {
          const firstCellKey = cellKeys[0];
          const cellLocationData = table[firstCellKey];
          if (
            cellLocationData &&
            Array.isArray(cellLocationData) &&
            cellLocationData[0]
          ) {
            const tablePage = cellLocationData[0].page || 1;
            setCurrentPage(tablePage);
          }
        }

        // Check if we have cached data for this table
        const cachedData = cachedTableData?.[tableId];

        let tableDataToSet;
        if (cachedData) {
          // Use cached modified data
          tableDataToSet = cachedData;
        } else {
          // Use original data from extraction result
          tableDataToSet = {
            headers: [...(table.headers || [])],
            rows: table.rows ? table.rows.map((row) => [...row]) : [],
          };
        }

        // Initialize IDs FIRST, then set table data
        initializeTableIdsInternal(tableDataToSet);
        setTableData(tableDataToSet);
      } else {
        setTableData(null);
        setColumnIds([]);
        setRowIds([]);
        setOriginalTableStructure(null);
      }
    },
    [
      tables,
      selectedTable,
      tableData,
      setSelectedTable,
      setSelectedCell,
      setEditingCell,
      setEditValue,
      clearHighlights,
      setDeletedColumns,
      setDeletedRows,
      setCurrentPage,
      setTableData,
      initializeTableIdsInternal,
      setColumnIds,
      setRowIds,
      setOriginalTableStructure,
      cachedTableData,
      setCachedTableData,
      setDynamicGridLines,
      setOriginalGridLines,
      setPendingGridLineChanges,
      setIsDraggingGridLine,
      setDraggingGridLine,
      setIsGridLineInPreview,
      setPendingNewGridLines,
      setIsPlacingGridLine,
    ]
  );

  return handleTableChange;
};

/**
 * Custom hook for handling global keyboard events during grid line operations
 * @param {object} params - Parameters object containing all dependencies
 */
export const useKeyDownHandler = ({
  isPlacingGridLine,
  isDraggingGridLine,
  isGridLineInPreview,
  cancelGridLinePlacement,
  cancelGridLineDrag,
  cancelGridLinePreview,
}) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (isPlacingGridLine) {
          e.preventDefault();
          e.stopPropagation();
          cancelGridLinePlacement();
        } else if (isDraggingGridLine) {
          e.preventDefault();
          e.stopPropagation();
          cancelGridLineDrag();
        } else if (isGridLineInPreview) {
          e.preventDefault();
          e.stopPropagation();
          cancelGridLinePreview();
        }
      }
    };

    if (isPlacingGridLine || isDraggingGridLine || isGridLineInPreview) {
      document.addEventListener("keydown", handleKeyDown, true); // Use capture phase
      return () => {
        document.removeEventListener("keydown", handleKeyDown, true);
      };
    }
  }, [
    isPlacingGridLine,
    isDraggingGridLine,
    isGridLineInPreview,
    cancelGridLinePlacement,
    cancelGridLineDrag,
    cancelGridLinePreview,
  ]);
};

/**
 * Custom hook for handling table data analysis submission workflow
 * Converts table data to CSV, uploads it, submits for AI analysis, and handles approval callback
 * @param {object} params - Parameters object containing all dependencies
 * @returns {function} handleSubmitTableForAnalysis function
 */
export const useTableAnalysisSubmissionHandler = ({
  setIsProcessingContinue,
  tableData,
  selectedTable,
  confidenceThreshold,
  confidenceFilterEnabled,
  onApprove,
  fileId,
  fileDownloadUrl,
  extractionResult,
}) => {
  const handleSubmitTableForAnalysis = useCallback(async () => {
    setIsProcessingContinue(true);

    try {
      // Step 1: Apply confidence filtering to table data if enabled
      let filteredTableData = tableData;
      if (confidenceFilterEnabled && selectedTable) {
        filteredTableData = applyConfidenceFilterToTableData(
          tableData,
          selectedTable,
          confidenceThreshold,
          confidenceFilterEnabled
        );
      }

      // Step 2: Store extraction results in S3 as JSON file (with filtered data)
      const originalFileName =
        fileDownloadUrl?.split("/").pop()?.split(".")[0] || "pdf_extraction";
      const filteredExtractionResult =
        confidenceFilterEnabled && selectedTable
          ? {
              ...extractionResult,
              data: {
                ...extractionResult?.data,
                tables: extractionResult?.data?.tables?.map((table) => ({
                  ...table,
                  data:
                    table.id === selectedTable?.id
                      ? filteredTableData
                      : table.data,
                })),
              },
            }
          : extractionResult;

      const {
        fileId: extractionResultsFileId,
        downloadUrl: extractionResultsDownloadUrl,
      } = await storeExtractionResults(
        filteredExtractionResult,
        originalFileName
      );

      // Step 3: Process table data: convert to CSV, upload, and submit for analysis (with confidence filtering)
      const {
        csvFileId,
        csvDownloadUrl,
        analysisResult,
      } = await processTableForAnalysis(
        filteredTableData,
        selectedTable,
        confidenceThreshold,
        confidenceFilterEnabled
      );

      // Pass the analysis result to the parent component with all file IDs and extraction details
      // CSV files should work with sheet name "original" by default
      if (onApprove) {
        const approvalData = {
          ...analysisResult.data,
          csvFileId: csvFileId, // Include the CSV file ID for subsequent table operations
          originalPdfFileId: fileId, // Include the original PDF file ID for downloads
          extractionResultsFileId: extractionResultsFileId, // Include extraction results file ID
          extractionDetails: {
            timestamp: new Date().toISOString(),
            provider: extractionResult?.data?.ocr_result?.provider || "unknown",
            originalPdfFileId: fileId,
            originalPdfFileUrl: fileDownloadUrl,
            generatedCsvFileId: csvFileId,
            generatedCsvFileUrl: csvDownloadUrl,
            extractionResultsFileId: extractionResultsFileId,
            extractionResultsFileUrl: extractionResultsDownloadUrl,
            confidenceFilterApplied: confidenceFilterEnabled,
            confidenceThreshold: confidenceFilterEnabled
              ? confidenceThreshold
              : null,
          },
        };

        onApprove(approvalData);
      }
    } catch (error) {
      throw error;
    } finally {
      setIsProcessingContinue(false);
    }
  }, [
    setIsProcessingContinue,
    tableData,
    selectedTable,
    confidenceThreshold,
    confidenceFilterEnabled,
    onApprove,
    fileId,
    fileDownloadUrl,
    extractionResult,
  ]);

  return handleSubmitTableForAnalysis;
};
